import { z } from "zod";

import apiClient from "../../../shared/api/client";

const timeSeriesPointSchema = z.object({
  period: z.string(),

  count: z.number(),
});

const integrationsStatsSchema = z.object({
  // Catálogo de plataformas que ofrecemos (Twitch/YouTube/Kick/Restream/Facebook).
  // Optional para no romper durante el deploy si el backend aún no lo devuelve.
  available: z.number().optional(),

  total: z.number(),

  active: z.number(),

  inactive: z.number(),

  daily: z.array(timeSeriesPointSchema),

  weekly: z.array(timeSeriesPointSchema),

  monthly: z.array(timeSeriesPointSchema),
});

const usersStatsSchema = z.object({
  total: z.number(),

  publicProfiles: z.number(),

  privateProfiles: z.number(),

  activeSessions: z.number(),

  inactiveSessions: z.number(),

  daily: z.array(timeSeriesPointSchema),

  weekly: z.array(timeSeriesPointSchema),

  monthly: z.array(timeSeriesPointSchema),

  // Spike-detection fields (added 2026-05-15 after the /auth/register abuse incident).
  // `spikeRatio` >= 5 surfaces a red banner in the Users tab.
  lastHour: z.number(),

  avgPerHourLast7d: z.number(),

  spikeRatio: z.number(),
});

const emailTemplateBreakdownSchema = z.object({
  template: z.string(),

  sent: z.number(),

  failed: z.number(),

  pending: z.number(),
});

const emailsStatsSchema = z.object({
  totalSent: z.number(),

  totalPending: z.number(),

  totalFailed: z.number(),

  daily: z.array(timeSeriesPointSchema),

  weekly: z.array(timeSeriesPointSchema),

  monthly: z.array(timeSeriesPointSchema),

  byTemplate: z.array(emailTemplateBreakdownSchema),
});

const workspacesStatsSchema = z.object({
  total: z.number(),

  public: z.number(),

  private: z.number(),

  active: z.number(),

  inactive: z.number(),

  daily: z.array(timeSeriesPointSchema),

  weekly: z.array(timeSeriesPointSchema),

  monthly: z.array(timeSeriesPointSchema),
});

const statsOverviewSchema = z.object({
  integrations: integrationsStatsSchema,

  users: usersStatsSchema,

  workspaces: workspacesStatsSchema,

  emails: emailsStatsSchema,
});

export type EmailTemplateBreakdown = z.infer<
  typeof emailTemplateBreakdownSchema
>;

export type EmailsStats = z.infer<typeof emailsStatsSchema>;

export type UsersStats = z.infer<typeof usersStatsSchema>;

export type TimeSeriesPoint = z.infer<typeof timeSeriesPointSchema>;

export type StatsOverview = z.infer<typeof statsOverviewSchema>;

export const fetchStatsOverview = async (): Promise<StatsOverview> => {
  const response = await apiClient.get("/stats/overview");

  return statsOverviewSchema.parse(response.data);
};

export const analyticsRangeSchema = z.enum(["live", "1d", "7d", "28d", "90d"]);

export const analyticsGranularitySchema = z.enum([
  "hour",

  "day",

  "week",

  "month",
]);

export const analyticsCardsSchema = z.object({
  activeUsers: z.number(),

  sessions: z.number(),

  pageViews: z.number(),

  eventsPerSession: z.number(),

  avgSessionDurationSec: z.number(),

  bounceRate: z.number(),
});

export const breakdownResponseSchema = z.object({
  data: z.array(
    z.object({
      label: z.string(),

      count: z.number(),
    }),
  ),
});

export const eventCountsResponseSchema = z.object({
  data: z.array(
    z.object({
      name: z.string(),

      count: z.number(),
    }),
  ),
});

export type EventCounts = z.infer<typeof eventCountsResponseSchema>["data"];

export type EventCount = EventCounts[number];

export const fetchEventCounts = async (
  range: AnalyticsRange,
): Promise<EventCounts> => {
  const response = await apiClient.get("/stats/analytics/events", {
    params: { range },
  });

  return eventCountsResponseSchema.parse(response.data).data;
};

export const desktopOverviewResponseSchema = z.object({
  data: z.object({
    installs: z.number(),

    sessions: z.number(),
  }),
});

export type DesktopOverview = z.infer<
  typeof desktopOverviewResponseSchema
>["data"];

export const fetchDesktopOverview = async (
  range: AnalyticsRange,
): Promise<DesktopOverview> => {
  const response = await apiClient.get("/stats/analytics/desktop/overview", {
    params: { range },
  });

  return desktopOverviewResponseSchema.parse(response.data).data;
};

export type DesktopProperty =
  | "cpu_model"
  | "cpu_cores"
  | "ram_gb"
  | "gpu_model"
  | "gpu_vendor"
  | "os_platform"
  | "os_release"
  | "arch"
  | "app_version";

export const fetchDesktopBreakdown = async (
  range: AnalyticsRange,

  property: DesktopProperty,

  limit = 10,
): Promise<Breakdown> => {
  const response = await apiClient.get("/stats/analytics/desktop/breakdown", {
    params: { range, property, limit },
  });

  return breakdownResponseSchema.parse(response.data).data;
};

export type BreakdownType =
  | "country"
  | "browser"
  | "os"
  | "device"
  | "language";

export type Breakdown = z.infer<typeof breakdownResponseSchema>["data"];

export type BreakdownRow = Breakdown[number];

export const fetchBreakdown = async (
  range: AnalyticsRange,

  type: BreakdownType,

  limit = 10,
): Promise<Breakdown> => {
  const response = await apiClient.get("/stats/analytics/breakdown", {
    params: { range, type, limit },
  });

  return breakdownResponseSchema.parse(response.data).data;
};

export const analyticsOverviewResponseSchema = z.object({
  data: z.object({
    cards: analyticsCardsSchema,

    timeseries: z.array(timeSeriesPointSchema),
  }),
});

export const topPagesResponseSchema = z.object({
  data: z.array(
    z.object({
      path: z.string(),

      pageViews: z.number(),

      avgEngagementTimeSec: z.number(),
    }),
  ),
});

export const topReferrersResponseSchema = z.object({
  data: z.array(
    z.object({
      source: z.string(),

      sessions: z.number(),
    }),
  ),
});

export type AnalyticsRange = z.infer<typeof analyticsRangeSchema>;

export type AnalyticsGranularity = z.infer<typeof analyticsGranularitySchema>;

export type AnalyticsCards = z.infer<typeof analyticsCardsSchema>;

export type AnalyticsOverviewResponse = z.infer<
  typeof analyticsOverviewResponseSchema
>;

export type AnalyticsOverview = AnalyticsOverviewResponse["data"];

export type TopPagesResponse = z.infer<typeof topPagesResponseSchema>;

export type TopPages = TopPagesResponse["data"];

export type TopPagePoint = TopPages[number];

export type TopReferrersResponse = z.infer<typeof topReferrersResponseSchema>;

export type TopReferrers = TopReferrersResponse["data"];

export type TopReferrerPoint = TopReferrers[number];

export const fetchAnalyticsOverview = async (
  range: AnalyticsRange,

  granularity: AnalyticsGranularity,
): Promise<AnalyticsOverview> => {
  const response = await apiClient.get("/stats/analytics/overview", {
    params: { range, granularity },
  });

  return analyticsOverviewResponseSchema.parse(response.data).data;
};

export const fetchTopPages = async (
  range: AnalyticsRange,

  limit = 10,
): Promise<TopPages> => {
  const response = await apiClient.get("/stats/analytics/top-pages", {
    params: { range, limit },
  });

  return topPagesResponseSchema.parse(response.data).data;
};

export const fetchTopReferrers = async (
  range: AnalyticsRange,

  limit = 10,
): Promise<TopReferrers> => {
  const response = await apiClient.get("/stats/analytics/top-referrers", {
    params: { range, limit },
  });

  return topReferrersResponseSchema.parse(response.data).data;
};
